import { findMatchGroups } from "./match.js";
import { tileColorToHex, cssToPhaser, Theme } from "../config/theme.js";
import { Settings } from "./Settings.js";

// ── Colour helpers ──────────────────────────────────────────────────────────
function lightenColor(hex, factor) {
  const r = (hex >> 16) & 0xff, g = (hex >> 8) & 0xff, b = hex & 0xff;
  return (Math.min(255, Math.floor(r + (255 - r) * factor)) << 16)
       | (Math.min(255, Math.floor(g + (255 - g) * factor)) << 8)
       |  Math.min(255, Math.floor(b + (255 - b) * factor));
}
function darkenColor(hex, factor) {
  const r = (hex >> 16) & 0xff, g = (hex >> 8) & 0xff, b = hex & 0xff;
  return (Math.floor(r * (1 - factor)) << 16)
       | (Math.floor(g * (1 - factor)) << 8)
       |  Math.floor(b * (1 - factor));
}

// ── Style: Classic — simple flat tile ───────────────────────────────────────
function drawGemClassic(gfx, S, colorHex) {
  const H = S / 2;
  gfx.clear();

  // 1. Dark outer border — crisp, well-defined
  gfx.fillStyle(darkenColor(colorHex, 0.50), 1);
  gfx.fillRoundedRect(-H, -H, S, S, 5);

  // 2. Flat colour body
  gfx.fillStyle(colorHex, 1);
  gfx.fillRoundedRect(-H + 3, -H + 3, S - 6, S - 6, 3);

  // 3. Sharp stroke for definition
  gfx.lineStyle(1.5, darkenColor(colorHex, 0.60), 0.9);
  gfx.strokeRoundedRect(-H, -H, S, S, 5);
}

// ── Style: Prism — faceted gem (original) ───────────────────────────────────
function drawGemPrism(gfx, S, colorHex) {
  const H = S / 2;
  gfx.clear();

  // 1. Dark outer border
  gfx.fillStyle(darkenColor(colorHex, 0.40), 1);
  gfx.fillRoundedRect(-H, -H, S, S, 7);

  // 2. Main colour body
  gfx.fillStyle(colorHex, 1);
  gfx.fillRoundedRect(-H + 3, -H + 3, S - 6, S - 6, 5);

  // 3. Top highlight facet (bright triangle pointing up)
  gfx.fillStyle(lightenColor(colorHex, 0.55), 0.65);
  gfx.beginPath();
  gfx.moveTo(0, -H + 7);
  gfx.lineTo(-H + 8, H * 0.15);
  gfx.lineTo( H - 8, H * 0.15);
  gfx.closePath();
  gfx.fillPath();

  // 4. Bottom shadow facet (dark triangle pointing down)
  gfx.fillStyle(0x000000, 0.25);
  gfx.beginPath();
  gfx.moveTo(0,  H - 7);
  gfx.lineTo(-H + 8, H * 0.15);
  gfx.lineTo( H - 8, H * 0.15);
  gfx.closePath();
  gfx.fillPath();
}

// ── Style: Themed — Prismara crystal cube with depth + glow ─────────────────
function drawGemThemed(gfx, S, colorHex) {
  const H  = S / 2;
  const TH = 5;           // thickness (visible bottom-right edge depth)
  const R  = 5;           // corner radius
  gfx.clear();

  // 1. Drop shadow (offset bottom-right, darkest)
  gfx.fillStyle(0x000000, 0.35);
  gfx.fillRoundedRect(-H + 3, -H + 3, S, S, R);

  // 2. Full block (bottom-right edge — darkest, creates the "thickness")
  gfx.fillStyle(darkenColor(colorHex, 0.55), 1);
  gfx.fillRoundedRect(-H, -H, S, S, R);

  // 3. Main front face — shifted upper-left, exposing thickness on bottom + right
  gfx.fillStyle(colorHex, 1);
  gfx.fillRoundedRect(-H, -H, S - TH, S - TH, R);

  // 4. Top edge highlight (top face catching light)
  gfx.fillStyle(lightenColor(colorHex, 0.40), 0.7);
  gfx.fillRoundedRect(-H, -H, S - TH, TH, { tl: R, tr: R, bl: 0, br: 0 });

  // 5. Left edge highlight (left face catching light)
  gfx.fillStyle(lightenColor(colorHex, 0.25), 0.5);
  gfx.fillRoundedRect(-H, -H, TH, S - TH, { tl: R, tr: 0, bl: R, br: 0 });

  // 6. Inner indent — large, shifted upper-left to show depth on bottom-right rim
  const IN = 4;                                    // small inset from front face edges
  const ix = -H + IN;                              // left edge of indent
  const iy = -H + IN;                              // top edge of indent
  const iw = S - TH - IN - 2;                      // wider — almost full face width
  const ih = S - TH - IN - 2;                      // taller — almost full face height

  // Indent body (slightly darker)
  gfx.fillStyle(darkenColor(colorHex, 0.14), 1);
  gfx.fillRoundedRect(ix, iy, iw, ih, 3);

  // Indent inner highlight — top + left rim (light catches the carved edge)
  gfx.fillStyle(lightenColor(colorHex, 0.50), 0.45);
  gfx.fillRoundedRect(ix, iy, iw, 2, { tl: 3, tr: 3, bl: 0, br: 0 });
  gfx.fillRoundedRect(ix, iy, 2, ih, { tl: 3, tr: 0, bl: 3, br: 0 });

  // Indent inner shadow — bottom + right rim (shadow inside the carve)
  gfx.fillStyle(0x000000, 0.25);
  gfx.fillRoundedRect(ix, iy + ih - 2, iw, 2, { tl: 0, tr: 0, bl: 3, br: 3 });
  gfx.fillRoundedRect(ix + iw - 2, iy, 2, ih, { tl: 0, tr: 3, bl: 0, br: 3 });

  // 7. Outer glow — subtle colour stroke on front face
  gfx.lineStyle(1.5, lightenColor(colorHex, 0.60), 0.35);
  gfx.strokeRoundedRect(-H, -H, S - TH, S - TH, R);
}

// ── Style dispatcher ────────────────────────────────────────────────────────
function drawGem(gfx, S, colorHex) {
  const style = Settings.tileStyle;
  if (style === 'classic') return drawGemClassic(gfx, S, colorHex);
  if (style === 'themed')  return drawGemThemed(gfx, S, colorHex);
  return drawGemPrism(gfx, S, colorHex);
}

// ── Texture cache — one GPU texture per (style + color + size) ──────────────
// Avoids 64 live Graphics objects replaying draw calls every frame.
function _getTexKey(style, colorHex, S) {
  return `gem_${style}_${colorHex.toString(16)}_${S}`;
}

function _ensureTexture(scene, S, colorHex) {
  const style = Settings.tileStyle;
  const key = _getTexKey(style, colorHex, S);
  if (scene.textures.exists(key)) return key;

  const H = S / 2;
  const pad = 6; // room for drop shadow + glow overshoot
  const texSize = S + pad * 2;

  // RenderTexture.draw() places the object at an explicit (x,y),
  // so the gem's (-H…+H) local draws land correctly inside the texture.
  const rt  = scene.add.renderTexture(-9999, -9999, texSize, texSize).setVisible(false);
  const gfx = scene.add.graphics();
  drawGem(gfx, S, colorHex);
  rt.draw(gfx, H + pad, H + pad);
  rt.saveTexture(key);
  gfx.destroy();
  rt.destroy();

  return key;
}

function createGemSprite(scene, x, y, S, colorHex) {
  const texKey = _ensureTexture(scene, S, colorHex);

  const container = scene.add.container(x, y);
  const img = scene.add.image(0, 0, texKey);
  container.add(img);
  container.setSize(S, S).setInteractive();
  container.on('pointerover', () =>
    scene.tweens.add({ targets: container, scale: 1.11, duration: 80, ease: 'Quad.Out' }));
  container.on('pointerout',  () =>
    scene.tweens.add({ targets: container, scale: 1.0,  duration: 100, ease: 'Quad.Out' }));

  // fillColor setter — swap the texture when color changes (shuffle)
  Object.defineProperty(container, 'fillColor', {
    set(val) {
      const newKey = _ensureTexture(scene, S, val);
      img.setTexture(newKey);
    },
    configurable: true,
  });
  return container;
}

export class Board {
  constructor(scene, opts) {
    this.scene = scene;
    this.rows = opts.rows;
    this.cols = opts.cols;
    this.tileSize = opts.tileSize;
    this.x = opts.x;
    this.y = opts.y;
    this.colors = opts.colors;

    this.grid = Array.from({ length: this.rows }, () => Array(this.cols).fill(null));

    this.highlightRect = scene.add.rectangle(0, 0, this.tileSize - 6, this.tileSize - 6)
      .setStrokeStyle(4, 0xffffff, 0.9)
      .setVisible(false);
  }

  generateWithoutInitialMatches() {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        this.spawnTile(r, c, true);
      }
    }

    let safety = 0;
    while (findMatchGroups(this.grid).all.length > 0 && safety < 60) {
      const matched = findMatchGroups(this.grid).all;
      for (const p of matched) this.replaceTile(p.r, p.c);
      safety++;
    }
  }

  worldPos(r, c) {
    return {
      x: this.x + c * this.tileSize + this.tileSize / 2,
      y: this.y + r * this.tileSize + this.tileSize / 2,
    };
  }

  randomColor(excludeColor = null) {
    const pool = excludeColor ? this.colors.filter(c => c !== excludeColor) : this.colors;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  spawnTile(r, c, noAnim = false) {
    const color = this.randomColor();
    const { x, y } = this.worldPos(r, c);

    const sprite = createGemSprite(this.scene, x, y, this.tileSize - 8, tileColorToHex(color));
    sprite.__tileData = { r, c };

    const tile = { r, c, color, sprite };
    this.grid[r][c] = tile;

    if (!noAnim) {
      sprite.scale = 0.3;
      this.scene.tweens.add({
        targets: sprite,
        scale: 1,
        duration: Theme.anim.spawnDuration,
        ease: "Back.Out(2.5)",
      });
    }
    return tile;
  }

  replaceTile(r, c) {
    const old = this.grid[r][c];
    const oldColor = old?.color ?? null;
    if (old?.sprite) old.sprite.destroy();
    const color = this.randomColor(oldColor);
    const { x, y } = this.worldPos(r, c);

    const sprite = createGemSprite(this.scene, x, y, this.tileSize - 8, tileColorToHex(color));
    sprite.__tileData = { r, c };

    this.grid[r][c] = { r, c, color, sprite };
  }

  highlight(r, c, on) {
    if (!on) {
      this.highlightRect.setVisible(false);
      return;
    }
    const { x, y } = this.worldPos(r, c);
    this.highlightRect.setPosition(x, y).setVisible(true);
  }

  swapInGrid(a, b) {
    const tA = this.grid[a.r][a.c];
    const tB = this.grid[b.r][b.c];
    this.grid[a.r][a.c] = tB;
    this.grid[b.r][b.c] = tA;

    if (tA) { tA.r = b.r; tA.c = b.c; tA.sprite.__tileData = { r: b.r, c: b.c }; }
    if (tB) { tB.r = a.r; tB.c = a.c; tB.sprite.__tileData = { r: a.r, c: a.c }; }
  }

  async swapAnimated(a, b) {
    const tA = this.grid[a.r][a.c];
    const tB = this.grid[b.r][b.c];
    if (!tA?.sprite || !tB?.sprite) return;

    const pA = this.worldPos(a.r, a.c);
    const pB = this.worldPos(b.r, b.c);

    await tweenPromise(this.scene, [tA.sprite, tB.sprite], {
      x: (target) => (target === tA.sprite ? pB.x : pA.x),
      y: (target) => (target === tA.sprite ? pB.y : pA.y),
      duration: Theme.anim.swapDuration,
      ease: "Back.Out(1.4)",
    });

    this.swapInGrid(a, b);
  }

  async removeTilesAnimated(positions) {
    const sprites = [];
    for (const p of positions) {
      const t = this.grid[p.r][p.c];
      if (t?.sprite) sprites.push(t.sprite);
    }

    await tweenPromise(this.scene, sprites, {
      scale: 0.1,
      alpha: 0,
      duration: Theme.anim.removeDuration,
      ease: "Sine.In",
    });

    for (const p of positions) {
      const t = this.grid[p.r][p.c];
      if (t?.sprite) t.sprite.destroy();
      this.grid[p.r][p.c] = null;
    }
  }
}

function tweenPromise(scene, targets, props) {
  return new Promise((resolve) => {
    if (!targets || targets.length === 0) return resolve();
    scene.tweens.add({
      targets,
      ...props,
      onComplete: () => resolve(),
    });
  });
}
